/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Mar 12 2024
 **/

import { useEffect } from "react";
import { isEmpty } from "lodash-es";

import { useChatRoomActions } from "@/frameworks/core/chat";
import { useFullSizeChatManager, useRoomViewActions, useSidebarActions } from "@/frameworks/core/hooks";
import { SidebarID } from "@/frameworks/core/commons";
import { Properties } from "@/frameworks/libraries/properties";
import { useChatDataStore } from "@/modules/chat/store";
import { useShallow } from "zustand/react/shallow";

export default function FullSizeChatLayer() {
	const { fullSizeChatActions, useFullSizeChatState } = useFullSizeChatManager();
	const { showFullSizeChat, setFullSizeChannelID } = useChatDataStore(
		useShallow((state) => ({
			showFullSizeChat: state.show_fullsize_chat,
			setFullSizeChannelID: state.set_fullsize_chat_id
		}))
	);

	const fullSizeChatState = useFullSizeChatState();

	const chatRoomActions = useChatRoomActions();
	const sidebarActions = useSidebarActions();
	const { updateRoomView } = useRoomViewActions();
	const chatSideBarState = sidebarActions.get(SidebarID.Chat);

	const openMiniChatPopup = () => {
		const fullSizeChatState = fullSizeChatActions.get();
		if (!fullSizeChatState) return;

		if (fullSizeChatState?.chatType === "DM") {
			chatRoomActions.common.open({ channelID: fullSizeChatState?.channelID, type: "DM", ignoreUpdate: true });
		} else {
			chatRoomActions.common.open({
				channelID: fullSizeChatState?.channelID || "",
				spaceID: fullSizeChatState?.spaceID || "",
				type: "Space",
				ignoreUpdate: true
			});
		}
	};

	// 풀사이즈 채팅 열려있는 상태에서 메뉴 변경시 열려있는 채팅 RoomView false 처리
	// ( 예외 사항 - 현재의 구조가 상태에 때른 useEffect 처리로 되어있어 현재로써 구조변경 안하면서 처리하려고 아래와 같이 처리함 )
	// 현재 풀사이즈 채팅 사용되는 영역이 Space와 Chat 사이드바 이기 때문에 메뉴 클릭하여 닫히는 처리는 두군데서만 필요함.
	useEffect(() => {
		[SidebarID.Chat, SidebarID.Space].forEach((eventID) => {
			sidebarActions.addOnBeforeHideEventListner(eventID, () => {
				return new Promise((resolve) => {
					if (!isEmpty(fullSizeChatState)) {
						updateRoomView({
							roomId: fullSizeChatState.channelID as string,
							deviceKey: Properties.browserTabID,
							view: false
						});
						setFullSizeChannelID("");
					}
					resolve();
					fullSizeChatActions.hide(true);
				});
			});
		});
	}, [fullSizeChatState]);

	useEffect(() => {
		if (showFullSizeChat) return;
		if (fullSizeChatState?.activityMessageID) return;

		openMiniChatPopup();
		fullSizeChatActions.hide(true);
	}, [chatSideBarState, showFullSizeChat, fullSizeChatState]);

	return null;
}
